
import {
  computed, defineComponent, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import InputSwitch from 'primevue/inputswitch';
import AutoRechargeFormDialog, {
  AutoRechargeFormMode,
  AutoRechargeFormValue,
} from '@/components/billing/dialogs/AutoRechargeFormDialog.vue';
import BillingConfirmDialog from '@/components/billing/shared/BillingConfirmDialog.vue';
import { setWalletAutoRecharge } from '@/composables/billing/billingApi';
import { WalletAutoRecharge } from '@/types/billing-types';
import { showToastError } from '@/helpers';
import { formatBillingAmount } from '@/composables/billing/formatAmount';

export default defineComponent({
  name: 'AutoRechargeBlock',

  components: {
    InputSwitch,
    AutoRechargeFormDialog,
    BillingConfirmDialog,
  },

  props: {
    autoRecharge: {
      type: Object as PropType<WalletAutoRecharge | null>,
      default: null,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const store = useStore();

    const submitting = ref(false);
    const formVisible = ref(false);
    const formMode = ref<AutoRechargeFormMode>('enable');
    const formError = ref<string | null>(null);
    const disableConfirmVisible = ref(false);

    const hasConfig = computed(() => (
      !!props.autoRecharge?.threshold && !!props.autoRecharge?.rechargeAmount
    ));

    const currency = computed<'EUR' | 'USD'>(
      () => (store.state.billing.overview?.shop?.currency === 'USD' ? 'USD' : 'EUR'),
    );
    const formatAmount = (n: number) => formatBillingAmount(n, currency.value);

    // ─── Toggle ──────────────────────────────────────────────────────────
    const onToggle = (next: boolean) => {
      if (submitting.value) return;
      if (next) {
        // Enable flow: always opens the form so the user confirms / sets
        // the values explicitly (even if previous values exist, we let them
        // review). Defaults seeded by the dialog when no value yet.
        formMode.value = 'enable';
        formError.value = null;
        formVisible.value = true;
      } else {
        // Disable flow: simple confirmation, then call setAutoRecharge.
        disableConfirmVisible.value = true;
      }
    };

    // ─── Edit ────────────────────────────────────────────────────────────
    const openEditForm = () => {
      if (submitting.value) return;
      formMode.value = 'edit';
      formError.value = null;
      formVisible.value = true;
    };

    // ─── Save (used by both enable and edit) ─────────────────────────────
    const onFormConfirm = async (v: AutoRechargeFormValue) => {
      submitting.value = true;
      formError.value = null;
      try {
        const result = await setWalletAutoRecharge({
          enabled: true,
          threshold: v.threshold,
          rechargeAmount: v.rechargeAmount,
          country: props.autoRecharge?.country ?? 'FR',
        });
        if (!result) {
          formError.value = t('billing.autoRechargeBlock.saveFailed');
          return;
        }
        await store.dispatch('billing/refetchOverview');
        formVisible.value = false;
      } catch {
        formError.value = t('billing.autoRechargeBlock.saveFailed');
      } finally {
        submitting.value = false;
      }
    };

    const onFormCancel = () => {
      formError.value = null;
    };

    // ─── Disable ─────────────────────────────────────────────────────────
    const onDisableConfirm = async () => {
      submitting.value = true;
      try {
        const result = await setWalletAutoRecharge({ enabled: false });
        if (!result) {
          showToastError(t('billing.autoRechargeBlock.saveFailed'));
          return;
        }
        await store.dispatch('billing/refetchOverview');
        disableConfirmVisible.value = false;
      } catch {
        showToastError(t('billing.autoRechargeBlock.saveFailed'));
      } finally {
        submitting.value = false;
      }
    };

    return {
      t,
      submitting,
      formVisible,
      formMode,
      formError,
      disableConfirmVisible,
      hasConfig,
      currency,
      formatAmount,
      onToggle,
      openEditForm,
      onFormConfirm,
      onFormCancel,
      onDisableConfirm,
    };
  },
});
